/** Copyright (c) 2019 T. Rowe Price. All rights reserved. */

import './ContactMethodMaintenanceModal.scss';

import classnames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import Helmet from 'react-helmet';

import Content from 'services/Content';
import { footerLinks } from 'services/Externals';
import BaseModal from 'components/shared/Modal/BaseModal';
import ContainerButtons from './ContainerButtons';
import { InvalidEmails, NoEmails } from './EmailAddresses';
import { InvalidMailingAddresses } from './MailingAddresses';
import VerificationInformation from './VerificationInformation';
import Optioner from 'components/shared/Optioner/Optioner';
import WithUserPermissions from 'components/shared/WithUserPermissions/WithUserPermissions';
import {
  getCustomer,
  getCustomerWebView
} from 'state/customer/CustomerSelectors';
import {
  getBaseModalTitle,
  getCMMEmailAddress,
  getContactMethodMaintenanceDisplayObject,
  getFilteredContactMethodMaintenance,
  getHasDataForContactMethodMaintenanceModal,
  getHasErrorsForContactMethodMaintenanceModal,
  getPhoneNumbers,
  getPrimaryMailingAddress
} from 'state/contactMethodMaintenance/ContactMethodMaintenanceSelectors';
import { irtMessagesHavePriority } from 'state/IRT/IRTMessagesSelectors';
import { isCMMModalNotPriority } from 'state/view/ViewSelectors';
import {
  verifyStopMailAddress,
  verifyContactInformation,
  verifyContactInformationV2
} from 'services/Rest';
import {
  contactMethodMaintenanceModalEnabled,
  residentialAddressEnabled
} from 'services/FeatureFlags';
import { NON_BREAKING_SPACE } from 'services/Formats';
import Store from 'state/Store';
import { analyticsPageOverlayLoaded } from 'state/analytics/AnalyticsActions';
import { annualVerificationHasInvalidMailing } from 'services/ContactMethodMaintenance';
import { getOwnsCollegeSavings, hasDeceasedAccount } from 'state/account/AccountSelectors';

const CMM_SESSION_STORAGE_ID = 'seenCMM';

const MailingAddressDisclaimer = () => {
  return <div className="cmm-address-disclaimer-content">
    {Content.mailingAddressDisclaimer}
  </div>
};

const ValidMailingAddressDisclaimer = () => {
  return <div className="cmm-valid-address-disclaimer">
    If your mailing address is valid,{NON_BREAKING_SPACE}<a href={footerLinks.contactUsUrl}>contact us</a>{NON_BREAKING_SPACE}to enable services on your account.
  </div>
};

class ContactMethodMaintenanceModal extends React.Component {
  state = {
    showModal:
      !JSON.parse(sessionStorage.getItem(CMM_SESSION_STORAGE_ID)) &&
      contactMethodMaintenanceModalEnabled()
  };

  componentDidUpdate(prevProps) {
    const { baseModalTitle } = this.props;
    if(prevProps.baseModalTitle !== baseModalTitle) {
      Store.dispatch(analyticsPageOverlayLoaded(baseModalTitle));
    }
  }

  closeModal = () => {
    this.setState({ showModal: false });
    sessionStorage.setItem(CMM_SESSION_STORAGE_ID, true);
  };

  closeModalAndVerify = () => {
    const {
      webViewEnabled,
      primaryMailingAddress,
      cmmEmailAddress,
      phoneNumbers,
      residentialAddress
    } = this.props;

    if (!webViewEnabled && !residentialAddressEnabled()) {
        verifyContactInformation(
        primaryMailingAddress,
        cmmEmailAddress,
        phoneNumbers
      );
    }

    if (!webViewEnabled && residentialAddressEnabled()) {
      verifyContactInformationV2(
        primaryMailingAddress,
        cmmEmailAddress,
        phoneNumbers,
        residentialAddress
      );
    }
    this.closeModal();
  };

  verifyStopMail = () => {
    const {
      primaryMailingAddress
    } = this.props;

    verifyStopMailAddress(
        primaryMailingAddress
    );

    this.closeModal();
  };

  render() {
    const {
      baseModalTitle,
      contactMethodMaintenanceFiltered,
      customer,
      displayObject,
      hasErrors,
      hasDataForModal,
      isIrtPriority,
      webViewEnabled,
      isCMMModalNotPriority,
      has529,
      hasDeceased
    } = this.props;
    const { showModal } = this.state;
    const {
      errors,
      hasAnnualVerification,
      hasInvalidEmailAddresses,
      hasInvalidMailingAddresses,
      hasInvalidMailingAndEmailAddresses,
      hasInvalidMailingAndNoEmailAddresses,
      hasNoEmailAddresses
    } = displayObject;

    const ContainerButtonsWithPermissions =
      WithUserPermissions(ContainerButtons);
    const className = classnames('cmm-modal-container', {
      'non-priority-modal-disabled': isCMMModalNotPriority
    });

    if (!showModal) {
      let bodyAttrs = { style: 'position: relative;' };
      return <Helmet bodyAttributes={bodyAttrs} />;
    }

    const validMailingAddressModalShows = has529 || hasDeceased;

    return (
      showModal &&
      !isEmpty(baseModalTitle) &&
      hasDataForModal &&
      !isIrtPriority && (
        <BaseModal
          hideClose={!webViewEnabled}
          containerClassName={className}
          isOpen={showModal}
          title={baseModalTitle}
          close={this.closeModal}
          disableClickOff={!webViewEnabled}
          hideAnchor>
          <Optioner
            val={{
              hasAnnualVerification,
              hasInvalidEmailAddresses,
              hasInvalidMailingAddresses,
              hasInvalidMailingAndEmailAddresses,
              hasInvalidMailingAndNoEmailAddresses,
              hasNoEmailAddresses
            }}>
            {{
              hasAnnualVerification: (
                <Fragment>
                  <VerificationInformation
                    contactMethodMaintenanceFiltered={
                      contactMethodMaintenanceFiltered
                    }
                    customer={customer}
                    errors={errors}
                    onClose={this.closeModal}
                  />
                  {annualVerificationHasInvalidMailing(errors) &&
                    <Fragment>
                      <div className="cmm-address-disclaimer-container">
                        <MailingAddressDisclaimer />
                        <ContainerButtonsWithPermissions
                          close={this.closeModal}
                          closeAndVerify={this.closeModalAndVerify}
                          showOnlyUpdate={hasErrors}
                          shouldDisable
                        />
                      </div>
                      <ValidMailingAddressDisclaimer />
                    </Fragment>
                  }
                  {!annualVerificationHasInvalidMailing(errors) &&
                    <ContainerButtonsWithPermissions
                      close={this.closeModal}
                      closeAndVerify={this.closeModalAndVerify}
                      showOnlyUpdate={hasErrors}
                      shouldDisable
                    />
                  }
                </Fragment>
              ),
              hasNoEmailAddresses: (
                <Fragment>
                  <div className="cmm-modal-content">
                    <NoEmails />
                  </div>
                  <ContainerButtons
                    close={this.closeModal}
                    closeAndVerify={this.closeModal}
                    showOnlyUpdate
                  />
                </Fragment>
              ),
              hasInvalidEmailAddresses: (
                <Fragment>
                  <div className="cmm-modal-content">
                    <InvalidEmails
                      emails={contactMethodMaintenanceFiltered.emailAddresses}
                    />
                  </div>
                  <ContainerButtons
                    close={this.closeModal}
                    closeAndVerify={this.closeModal}
                    showOnlyUpdate
                  />
                </Fragment>
              ),
              hasInvalidMailingAddresses: (
                <Fragment>
                  <div className="cmm-modal-content">
                    {Content.mailingAddressInformation}
                    <InvalidMailingAddresses
                      address={
                        contactMethodMaintenanceFiltered.mailingAddresses
                      }
                      addressLabel="Mailing Address"
                    />
                  </div>
                  <div className="cmm-address-disclaimer-container">
                    <MailingAddressDisclaimer />
                    <ContainerButtonsWithPermissions
                      close={this.closeModal}
                      closeAndVerify={this.verifyStopMail}
                      has529={has529}
                      hasDeceased={hasDeceased}
                      showOnlyUpdate
                      isInvalidMailing
                      shouldDisable
                    />
                  </div>
                  {validMailingAddressModalShows && <ValidMailingAddressDisclaimer />}
                </Fragment>
              ),
              hasInvalidMailingAndNoEmailAddresses: (
                <Fragment>
                  <div className="cmm-modal-content">
                    <InvalidMailingAddresses
                      address={
                        contactMethodMaintenanceFiltered.mailingAddresses
                      }
                      addressLabel="Mailing Address"
                    />
                    <NoEmails />
                  </div>
                  <div className="cmm-address-disclaimer-container">
                    <MailingAddressDisclaimer />
                    <ContainerButtons
                      close={this.closeModal}
                      closeAndVerify={this.closeModal}
                      showOnlyUpdate
                    />
                  </div>
                  <ValidMailingAddressDisclaimer />
                </Fragment>
              ),
              hasInvalidMailingAndEmailAddresses: (
                <Fragment>
                  <div className="cmm-modal-content">
                    <InvalidMailingAddresses
                      address={
                        contactMethodMaintenanceFiltered.mailingAddresses
                      }
                      addressLabel="Mailing Address"
                    />
                    <InvalidEmails
                      emails={contactMethodMaintenanceFiltered.emailAddresses}
                    />
                  </div>
                  <div className="cmm-address-disclaimer-container">
                    <MailingAddressDisclaimer />
                    <ContainerButtons
                      close={this.closeModal}
                      closeAndVerify={this.closeModal}
                      showOnlyUpdate
                    />
                  </div>
                  <ValidMailingAddressDisclaimer />
                </Fragment>
              )
            }}
          </Optioner>
        </BaseModal>
      )
    );
  }
}

const mapStateToProps = (state) => ({
  baseModalTitle: getBaseModalTitle(state),
  contactMethodMaintenanceFiltered: getFilteredContactMethodMaintenance(state),
  customer: getCustomer(state),
  displayObject: getContactMethodMaintenanceDisplayObject(state),
  hasErrors: getHasErrorsForContactMethodMaintenanceModal(state),
  hasDataForModal: getHasDataForContactMethodMaintenanceModal(state),
  isIrtPriority: irtMessagesHavePriority(state),
  webViewEnabled: getCustomerWebView(state),
  isCMMModalNotPriority: isCMMModalNotPriority(state),
  primaryMailingAddress: getPrimaryMailingAddress(state),
  cmmEmailAddress: getCMMEmailAddress(state),
  phoneNumbers: getPhoneNumbers(state),
  has529: getOwnsCollegeSavings(state),
  hasDeceased: hasDeceasedAccount(state)
});

export default connect(mapStateToProps)(ContactMethodMaintenanceModal);
